import React, { useState, useRef, useCallback } from 'react';
import { Modal, Button, Upload, message } from 'antd';
import { UploadOutlined, RotateLeftOutlined, RotateRightOutlined } from '@ant-design/icons';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './index.css';

interface ImageCropModalProps {
  visible: boolean;
  onCancel: () => void;
  onCropComplete: (croppedImageFile: File) => void;
  aspectRatio?: number; // 4:3 = 1.333
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  visible,
  onCancel,
  onCropComplete,
  aspectRatio = 4 / 3,
}) => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const onSelectFile = (file: File) => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      message.error('ขนาดไฟล์ต้องไม่เกิน 10MB');
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      return;
    }
    
    if (file) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setOriginalImage(reader.result?.toString() || null);
      });
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = useCallback((img: HTMLImageElement) => {
    imgRef.current = img;
  }, []);

  const getCroppedImg = useCallback(
    (image: HTMLImageElement, crop: PixelCrop): Promise<Blob> => {
      const canvas = canvasRef.current;
      if (!canvas || !crop.width || !crop.height) {
        return Promise.reject(new Error('Canvas or crop dimensions not available'));
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return Promise.reject(new Error('No 2d context'));
      }

      // Calculate the actual dimensions based on crop
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Set canvas size to the desired output size (maintaining 4:3 ratio)
      const outputWidth = 800; // Fixed width
      const outputHeight = outputWidth / aspectRatio; // Calculate height based on aspect ratio
      
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Save the context state
      ctx.save();

      // Apply rotation if needed
      if (rotation !== 0) {
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        ctx.translate(centerX, centerY);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-centerX, -centerY);
      }

      // Draw the cropped image
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );

      // Restore the context state
      ctx.restore();

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          }
        }, 'image/jpeg', 0.9);
      });
    },
    [aspectRatio, rotation]
  );

  const handleCropComplete = async () => {
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      message.error('กรุณาเลือกพื้นที่ที่ต้องการครอป');
      return;
    }

    try {
      setLoading(true);
      const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop);
      
      // Convert blob to file
      const file = new File([croppedImageBlob], 'cropped-image.jpg', {
        type: 'image/jpeg',
      });

      onCropComplete(file);
      handleClose();
    } catch (error) {
      console.error('Error cropping image:', error);
      message.error('เกิดข้อผิดพลาดในการครอปรูปภาพ');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOriginalImage(null);
    setCrop(undefined);
    setCompletedCrop(null);
    setRotation(0);
    onCancel();
  };

  const handleRotate = (degree: number) => {
    setRotation((prev) => (prev + degree) % 360);
  };

  return (
    <>
      <Modal
        title="ปรับขนาดรูปภาพ (4:3)"
        open={visible}
        onCancel={handleClose}
        width={800}
        centered
        footer={[
          <Button key="cancel" onClick={handleClose}>
            ยกเลิก
          </Button>,
          <Button
            key="crop"
            type="primary"
            loading={loading}
            disabled={!completedCrop}
            onClick={handleCropComplete}
          >
            ยืนยันการครอป
          </Button>,
        ]}
      >
        <div className="image-crop-container">
          {!originalImage ? (
            <div className="upload-area">
              <Upload.Dragger
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  onSelectFile(file);
                  return false; // Prevent automatic upload
                }}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">คลิกหรือลากไฟล์รูปภาพมาที่นี่</p>
                <p className="ant-upload-hint">
                  รองรับไฟล์ JPG, PNG, GIF (ขนาดไม่เกิน 10MB)
                </p>
              </Upload.Dragger>
            </div>
          ) : (
            <div className="crop-area">
              <div className="crop-controls">
                <Button
                  icon={<RotateLeftOutlined />}
                  onClick={() => handleRotate(-90)}
                  size="small"
                >
                  หมุนซ้าย
                </Button>
                <Button
                  icon={<RotateRightOutlined />}
                  onClick={() => handleRotate(90)}
                  size="small"
                >
                  หมุนขวา
                </Button>
                <Button
                  onClick={() => setOriginalImage(null)}
                  size="small"
                >
                  เลือกรูปใหม่
                </Button>
              </div>
              
              <div className="crop-wrapper">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspectRatio}
                  minWidth={100}
                  minHeight={100 / aspectRatio}
                >
                  <img
                    ref={imgRef}
                    alt="Crop"
                    src={originalImage}
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      maxHeight: '400px',
                      maxWidth: '100%',
                    }}
                    onLoad={(e) => onImageLoad(e.currentTarget)}
                  />
                </ReactCrop>
              </div>
              
              <div className="crop-info">
                <p>คลิกและลากเพื่อเลือกพื้นที่ที่ต้องการครอป (อัตราส่วน 4:3)</p>
                <p>ขนาดผลลัพธ์: 800 x 600 พิกเซล</p>
              </div>
            </div>
          )}
        </div>
      </Modal>
      
      {/* Hidden canvas for processing */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default ImageCropModal;